"use client";

import { CreditCardIcon } from "@heroicons/react/24/outline";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import Link from "next/link";
import { usePostHog } from "posthog-js/react";
import { FC, useContext, useEffect, useRef, useState } from "react";
import {
  failedRenewalSentence,
  FailedPayment,
  FailedPaymentWallAction,
  findFailedPayment,
  findPayableSubscription,
  onFailedPaymentWallOpen,
  SubscriptionsResponse,
} from "../helpers/failedPaymentWall";
import { pastDueAccessEndsAt } from "../helpers/pastDueSubscription";
import { ModalContext } from "../hooks/useModal";
import { useSubscriptions } from "../hooks/useSubscriptions";
import {
  UpdatePaymentResult,
  UpdatePaymentSource,
  useUpdatePayment,
} from "../hooks/useUpdatePayment";
import ConfirmAction from "./ConfirmAction";
import Dialog from "./Dialog";
import Typography from "./Typography";

export type FailedPaymentWallTrigger = "blocked_action" | "landing";

type FailedPaymentWallViewProps = {
  failedPayment?: FailedPayment | null;
  /** When FlyCode cancels the subscription; dropped when unknown. */
  cancelsAt?: Date | null;
  updating?: boolean;
  onUpdate?: () => void;
  onClose?: () => void;
};

// The wall's content: what failed, what's paused, and the one way out.
export const FailedPaymentWallView: FC<FailedPaymentWallViewProps> = ({
  failedPayment,
  cancelsAt,
  updating,
  onUpdate,
  onClose,
}) => (
  <ConfirmAction
    leading={
      <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-full bg-red-50 text-red-600">
        <CreditCardIcon className="h-5 w-5" aria-hidden />
      </div>
    }
    title="Update your payment method"
    description={`${failedRenewalSentence(failedPayment)} Update your card to keep creating.${
      cancelsAt
        ? ` Your subscription will be cancelled on ${dayjs(cancelsAt).format("MMMM D")}.`
        : ""
    }`}
    confirmLabel="Update payment"
    cancelLabel="Not now"
    confirmLoading={updating}
    onConfirm={onUpdate}
    onCancel={onClose}
  >
    <Typography variant="body2" className="mt-3 text-foreground-secondary">
      Generating, exporting and publishing are paused until the payment goes
      through. Your clips, drafts and downloads are still here.
    </Typography>
    <Typography variant="caption" className="mt-4 text-foreground-muted">
      Questions?{" "}
      <a className="underline" href="mailto:support@klap.app">
        Contact support
      </a>{" "}
      or{" "}
      <Link className="underline" href="/billing" onClick={onClose}>
        manage your plan
      </Link>
      .
    </Typography>
  </ConfirmAction>
);

// The wall's "Update payment" (exported for tests). Nothing to pay after
// all (klap-api's "nothing_due", e.g. an old past-due plan a newer active
// one replaced): the wall closes, and the hook says the payment is up to
// date.
export const updatePaymentFromWall = async (
  updatePayment: (
    source: UpdatePaymentSource,
    properties: Record<string, unknown>,
  ) => Promise<UpdatePaymentResult>,
  properties: Record<string, unknown>,
  close?: () => void,
): Promise<UpdatePaymentResult> => {
  const result = await updatePayment("wall", properties);
  if (result === "nothing_due") close?.();
  return result;
};

type FailedPaymentWallProps = {
  trigger?: FailedPaymentWallTrigger;
  action?: FailedPaymentWallAction;
  /** Defaults to closing the app modal it was opened in. */
  onClose?: () => void;
};

// The wall with the signed-in user's failed renewal (GET
// /billing/subscription). Opened in the app modal by the routes' 402
// handlers (helpers/pricingLimitDialog.tsx), or by FailedPaymentWallHost.
const FailedPaymentWall: FC<FailedPaymentWallProps> = ({
  trigger = "blocked_action",
  action,
  onClose,
}) => {
  const posthog = usePostHog();
  const modal = useContext(ModalContext);
  const queryClient = useQueryClient();
  const { data } = useSubscriptions();
  const { updatePayment, isPending } = useUpdatePayment();

  const response = data as SubscriptionsResponse | undefined;
  const failedPayment = findFailedPayment(response);
  const pastDueSubscription = findPayableSubscription(response);
  const cancelsAt =
    pastDueSubscription?.status === "past_due"
      ? pastDueAccessEndsAt(pastDueSubscription)
      : null;
  const close = onClose ?? modal?.close;
  const eventProperties = {
    trigger,
    action: action ?? null,
    days_past_due: response?.failed_payment_wall?.days_past_due ?? null,
    plan: failedPayment?.plan ?? null,
    amount_due: failedPayment?.amount_due ?? null,
    currency: failedPayment?.currency ?? null,
  };

  // Once per opening.
  const shown = useRef(false);
  useEffect(() => {
    if (shown.current) return;
    shown.current = true;
    posthog?.capture("Failed Payment Wall Showed", eventProperties);
    // The banner may still hold a list from before the wall applied.
    queryClient.invalidateQueries({ queryKey: ["subscriptions"] });
  }, [posthog, queryClient, eventProperties]);

  return (
    <FailedPaymentWallView
      failedPayment={failedPayment}
      cancelsAt={cancelsAt}
      updating={isPending}
      onUpdate={() => updatePaymentFromWall(updatePayment, eventProperties, close)}
      onClose={close}
    />
  );
};

// Shows the wall over everything (even the app modal) when a request helper
// outside React calls openFailedPaymentWall(). Mounted once, app-wide.
export const FailedPaymentWallHost: FC = () => {
  const [opening, setOpening] = useState<{ action?: FailedPaymentWallAction } | null>(
    null,
  );
  useEffect(
    () => onFailedPaymentWallOpen((action) => setOpening({ action })),
    [],
  );
  const close = () => setOpening(null);
  return (
    <Dialog
      isOpen={!!opening}
      onClose={close}
      // The app modal's overlay sits at z-[9999].
      overlayClassName="!z-[10000]"
      className="max-w-md"
    >
      {opening ? <FailedPaymentWall action={opening.action} onClose={close} /> : null}
    </Dialog>
  );
};

export default FailedPaymentWall;
